import React, { useState, useCallback, useRef, useEffect } from 'react';
// @ts-ignore - tui-image-editor doesn't have TypeScript types
import ImageEditor from 'tui-image-editor';
import 'tui-image-editor/dist/tui-image-editor.css';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { apiClient } from '@/services/api';
import { toast } from 'react-hot-toast';

interface ImageEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  onSave: (newImageUrl: string) => void;
}

export const ImageEditorModal: React.FC<ImageEditorModalProps> = ({
  isOpen,
  onClose,
  imageSrc,
  onSave,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const editorRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize editor when modal opens
  useEffect(() => {
    if (!isOpen || !imageSrc) {
      return;
    }

      // Wait for the container to be available
    let timeoutId: NodeJS.Timeout | null = null;
    let isMounted = true;

    const initEditor = () => {
      if (!isMounted) return;
      
      if (!containerRef.current) {
        // Retry after a short delay
        timeoutId = setTimeout(initEditor, 50);
        return;
      }

      // Clean up existing editor if any
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }

      try {
        const editor = new ImageEditor(containerRef.current, {
          includeUI: {
            menu: ['crop', 'flip', 'rotate', 'draw', 'shape', 'icon', 'text'],
            initMenu: 'shape',
            menuBarPosition: 'bottom',
          },
          cssMaxWidth: 700,
          cssMaxHeight: 500,
          selectionStyle: {
            cornerSize: 20,
            rotatingPointOffset: 70,
          },
        });

        editorRef.current = editor;

        // Wait a bit for editor to fully initialize, then load image
        setTimeout(() => {
          editor.loadImageFromURL(imageSrc, 'Image')
            .then((result: any) => {
              // Resize editor to match image dimensions
              if (result && editor.ui) {
                editor.ui.resizeEditor({
                  imageSize: {
                    oldWidth: result.oldWidth || 0,
                    oldHeight: result.oldHeight || 0,
                    newWidth: result.newWidth || result.oldWidth || 0,
                    newHeight: result.newHeight || result.oldHeight || 0,
                  },
                });
              }
              // Activate menu to enable all controls
              if (editor.ui) {
                editor.ui.activeMenuEvent();
              }
            })
            .catch(() => {
              toast.error('Failed to load image. Please check CORS settings on S3 bucket.');
            });
        }, 200);
      } catch (error) {
        toast.error('Failed to initialize image editor');
      }
    };

    timeoutId = setTimeout(initEditor, 100);

    // Cleanup on unmount
    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, [isOpen, imageSrc]);

  const handleSave = useCallback(async () => {
    if (!editorRef.current) {
      toast.error('Editor not ready');
      return;
    }

    setIsSaving(true);
    try {
      // Get the edited image as base64
      const dataURL = editorRef.current.toDataURL();
      
      if (!dataURL) {
        toast.error('Failed to export edited image');
        setIsSaving(false);
        return;
      }

      // Extract MIME type and base64 data
      const [prefix, base64String] = dataURL.split(',');
      const mimeMatch = prefix.match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : 'image/png';
      
      // Convert base64 to blob
      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mime });

      // Create a File from the blob
      const file = new File([blob], 'edited-image.png', { type: 'image/png' });

      // Upload the edited image
      const response = await apiClient.uploadAsset(file);
      
      if (response.ok && response.asset) {
        toast.success('Image edited and uploaded successfully');
        onSave(response.asset.url);
        onClose();
      } else {
        toast.error(response.error || 'Failed to upload edited image');
      }
    } catch (error) {
      toast.error('Failed to save edited image');
    } finally {
      setIsSaving(false);
    }
  }, [onSave, onClose]);

  const handleClose = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.destroy();
      editorRef.current = null;
    }
    onClose();
  }, [onClose]);

  return (
    <>
      <style>{`
        [data-radix-dialog-overlay][data-state="open"] {
          z-index: 9999 !important;
        }
        [data-radix-dialog-content][data-state="open"] {
          z-index: 10000 !important;
        }
        .tui-image-editor-header-buttons,
        .tui-image-editor-header-logo {
          display: none !important;
        }
      `}</style>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent 
          className="max-w-[95vw] max-h-[95vh] w-full h-full p-0"
          style={{ zIndex: 10000 }}
        >
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle>Edit Image</DialogTitle>
        </DialogHeader>
        <div 
          ref={containerRef}
          className="flex-1 overflow-hidden"
          style={{ height: 'calc(95vh - 120px)', minHeight: '500px' }}
        />
        <DialogFooter className="px-6 pb-6 pt-0">
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'OK'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};
